import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import type { IconType } from '@factorialco/factorial-one-react'
import { OneDataCollection, RecordType } from '@factorialco/factorial-one-react/dist/experimental'
import { Plus } from '@factorialco/factorial-one-react/icons/app'
import { NodePageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CompetenciesFinder, Competency } from 'modules/competencies/components/CompetenciesFinder'
import { CompetencyConfig } from 'modules/competencies/hooks/useCompetencyFinder'
import type { CompetencySuggestionTarget } from 'modules/competencies/hooks/useFactorialCompetenciesSuggestions'
import { NodeTabs } from 'modules/job_catalog/components/NodeTabs'
import useAssignJobCatalogTreeNodeAttributes, {
  serializeCompetency,
} from 'modules/job_catalog/hooks/useAssignJobCatalogTreeNodeAttributes'
import {
  NODE_TYPES,
  NodeType,
  TreeNode,
  findNodeByUuid,
  formatUuidToNodeType,
  getChildNodeType,
  getTextFromNodeType,
  isNodeType,
  nodeTypeIcon,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface Item extends RecordType {
  title: string
  description: string
  icon: IconType
  definedAt: string
}

const CompetenciesPage = () => {
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = levelId || roleId || functionId || familyId || rootId || ''

  const childType = getChildNodeType(id)

  const [totalCompetencies, setTotalCompetencies] = useState(0)
  const [currentCompetencies, setCurrentCompetencies] = useState<CompetencyConfig[]>([])
  const [showCompetenciesModal, setShowCompetenciesModal] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const { company } = useCurrentSession()

  const { assignAttributes } = useAssignJobCatalogTreeNodeAttributes()

  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([])

  const target: CompetencySuggestionTarget | undefined = useMemo(() => {
    const node = findNodeByUuid(treeNodes, id)
    if (!node || node.type != 'jobcatalog_treerole') return
    return {
      type: 'jobcatalog_treerole',
      id: node.id,
      title: node.name,
      description: node.description ?? '',
    }
  }, [treeNodes, id])

  const source = useGraphqlDataSource(
    {
      document: NodePageDocument,
      mapData: (data) => {
        const attributes = data?.jobCatalog?.nodeAttributesConnection?.nodes ?? []
        const competencies = attributes
          .filter((attr) => attr.type.toLowerCase() === 'competency')
          .filter((attr) => attr.takesPrecedence)
          .map((attr) => {
            const parsed = JSON.parse(attr.serializedValue || '{}')
            const normalizedType = normalizeNodeType(attr.definedAt)
            const type: NodeType = isNodeType(normalizedType) ? normalizedType : NODE_TYPES.ROOT

            const attributeId = attr.attributeId ? +attr.attributeId : 0
            return {
              id: attributeId,
              title: parsed?.title ?? '-',
              description: parsed?.description ?? '-',
              definedAt: getTextFromNodeType(type),
              icon: nodeTypeIcon(type),
              takesPrecedence: attr.takesPrecedence,
            }
          })

        const serializedTree: string | undefined =
          data.jobCatalog.treesConnection.nodes[0]?.serializedTree
        if (serializedTree) setTreeNodes([JSON.parse(serializedTree)])

        setCurrentCompetencies(
          competencies.map((competency) => ({
            id: competency.id,
            readonly:
              getTextFromNodeType(normalizeNodeType(competency.definedAt)) !==
              getTextFromNodeType(normalizeNodeType(id)),
          }))
        )
        setTotalCompetencies(competencies.length)
        return competencies
      },
      mapPagination: (data) => ({
        total:
          data.jobCatalog.nodeAttributesConnection.nodes
            .filter((attr) => attr.type.toLowerCase() === 'competency')
            .filter((attr) => attr.takesPrecedence).length ?? 0,
      }),
      primaryActions: () => ({
        label: i18n.t('job_catalog.nodes.actions.new'),
        onClick: () => setShowCompetenciesModal(true),
        icon: Plus,
      }),
      mapVariables: ({ search }) => ({
        search,
        nodeId: Number(id?.split('-')[1]),
        nodeType: formatUuidToNodeType(id || ''),
        childType: formatUuidToNodeType(childType || ''),
      }),
    },
    [refreshTrigger]
  )
  const submitCompetencies = async (competencies: Competency[]) => {
    const selectedCompetencies = competencies.filter((competency) => {
      const current = currentCompetencies.find((c) => c.id === competency.id)
      return (current && !current.readonly) || !current
    })
    const result = await assignAttributes(
      'competency',
      company.id,
      id,
      selectedCompetencies.map(serializeCompetency)
    )
    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.competency.success'),
        time: 5000,
      })
      setRefreshTrigger((prev) => prev + 1)
    }
    setShowCompetenciesModal(false)
  }

  const isItem = (record: RecordType): record is Item => {
    return 'title' in record && 'description' in record && 'icon' in record && 'definedAt' in record
  }

  return (
    <ResponsivePage.Body variant="full">
      <NodeTabs />
      <Box paddingTop="s20">
        <Text.Header3>
          {i18n.tp('job_catalog.nodes.competency', {
            count: totalCompetencies,
          })}
        </Text.Header3>
      </Box>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('job_catalog.nodes.competency.one'),
                  render: (record: RecordType) => {
                    if (!isItem(record)) return { type: 'text', value: '' }
                    return {
                      type: 'text',
                      value: record.title,
                    }
                  },
                },
                {
                  label: i18n.t('job_catalog.nodes.description'),
                  render: (record: RecordType) => {
                    if (!isItem(record)) return { type: 'text', value: '' }
                    return {
                      type: 'text',
                      value: record.description,
                    }
                  },
                },
                {
                  label: '',
                  render: (record: RecordType) => {
                    if (!isItem(record)) return { type: 'tag', value: { label: '' } }
                    return {
                      type: 'tag',
                      value: { label: record.definedAt, icon: record.icon },
                    }
                  },
                },
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
      {showCompetenciesModal && (
        <CompetenciesFinder
          currentCompetencies={currentCompetencies}
          onSubmit={submitCompetencies}
          onClose={() => setShowCompetenciesModal(false)}
          target={target}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default CompetenciesPage
